import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { css } from 'glamor';
import { DateTime } from 'luxon';
import { useQuery, useMutation } from '@apollo/client';
import { useHistory, useRouteMatch } from 'react-router-dom';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';

import { Loading, LoadingError } from '../../components/LoadingFork';
import { Table, THead, TBody } from '../../components/Table';
import { Well } from '../../components/Well';
import { Button, HollowButton } from '../../components/Button';
import { withInstallation } from '../../components/InstallationContext';
import authManager, { PERMISSIONS } from '../../lib/authManager';
import { isParklioBayAccessEnabled } from '../../lib/parklioFeature';
import { StateDot } from '../../components/bayAccess/StateDot';
import { ReasonDialog } from '../../components/bayAccess/ReasonDialog';

import { listParkingOperationsBays, listBayTimeline } from './queries';
import { setBayOutOfServiceMutation, returnBayToServiceMutation } from './mutations';
import { ActivityLog } from './ActivityLog';
import { BayInventory, deviceDescriptor } from './BayInventory';
import { styles } from './styles';
import { parklioBookingBarrierOverrideMutation } from '../Booking/mutations';
import { OVERRIDE_REASONS } from '../../components/bayAccess/constants/overrideReasons';
import { OUTCOME_MESSAGE, UNKNOWN_OUTCOME_MESSAGE } from '../../components/bayAccess/constants/display';

const SCALES = ['Hours', 'Days', 'Week', 'Month'];

const HOUR_MS = 60 * 60 * 1000;

// Every calendar boundary and label is resolved in the installation's timezone
// (installation.timeZoneName), not the browser's, so remote staff see the hotel's
// midnight-to-midnight day. tz falls back to the system zone when absent.
function zoned(ms, tz) {
    return DateTime.fromMillis(ms, { zone: tz || 'local' });
}

function hhmm(ms, tz) {
    return zoned(ms, tz).toFormat('HH:mm');
}

function nextMidnight(ms, tz) {
    return zoned(ms, tz).startOf('day').plus({ days: 1 }).toMillis();
}

// Tile [dayStart, dayEnd) with step-hour cells, clamping the last to dayEnd. Driving
// the end off the next calendar midnight keeps DST days (23h/25h) gap- and overlap-free.
function tileCells(dayStart, dayEnd, step, tz) {
    const cells = [];
    let cellStart = dayStart;
    while (cellStart < dayEnd) {
        const cellEnd = Math.min(cellStart + step * HOUR_MS, dayEnd);
        cells.push({ start: cellStart, end: cellEnd, label: hhmm(cellStart, tz) });
        cellStart = cellEnd;
    }
    return cells;
}

function hourCells(dayStart, step, tz) {
    return tileCells(dayStart, nextMidnight(dayStart, tz), step, tz);
}

function dayCells(startMs, count, labelFn, tz) {
    const base = zoned(startMs, tz).startOf('day');
    return Array.from({ length: count }, (unused, index) => {
        const dayStart = base.plus({ days: index });
        return {
            start: dayStart.toMillis(),
            end: dayStart.plus({ days: 1 }).toMillis(),
            label: labelFn(dayStart),
        };
    });
}

// Window + two-row axis per scale, mirroring Nick's buildSlots: Days = 3 days of
// 3-hour cells, Week = 7 days from the selected day, Month = the whole calendar
// month, Hours = one day. Cells fill the width (no horizontal scroll).
function buildSlots(dayMs, mode, tz) {
    const base = zoned(dayMs, tz);
    const dayStart = base.startOf('day').toMillis();

    if (mode === 'Month') {
        const monthStartDt = base.startOf('month');
        const monthStart = monthStartDt.toMillis();
        const cells = dayCells(monthStart, monthStartDt.daysInMonth, (date) => String(date.day), tz);
        const label = monthStartDt.setLocale('en-GB').toLocaleString({ month: 'long', year: 'numeric' });
        return { start: monthStart, end: cells[cells.length - 1].end, cells, groups: [{ key: 0, label, span: cells.length }] };
    }
    if (mode === 'Week') {
        const cells = dayCells(dayStart, 7, (date) => date.setLocale('en-GB').toLocaleString({ weekday: 'short', day: 'numeric', month: 'short' }), tz);
        const startLabel = zoned(dayStart, tz).setLocale('en-GB').toLocaleString({ day: 'numeric', month: 'short' });
        const endLabel = zoned(cells[6].start, tz).setLocale('en-GB').toLocaleString({ day: 'numeric', month: 'short', year: 'numeric' });
        return { start: dayStart, end: cells[6].end, cells, groups: [{ key: 0, label: `${startLabel} – ${endLabel}`, span: 7 }] };
    }
    if (mode === 'Days') {
        const DAYS = 3;
        const STEP = 3;
        const groups = [];
        const cells = [];
        for (let dayIndex = 0; dayIndex < DAYS; dayIndex += 1) {
            const dStart = base.startOf('day').plus({ days: dayIndex }).toMillis();
            const dayCellList = tileCells(dStart, nextMidnight(dStart, tz), STEP, tz);
            groups.push({ key: dayIndex, label: zoned(dStart, tz).setLocale('en-GB').toLocaleString({ weekday: 'short', day: 'numeric', month: 'short' }), span: dayCellList.length });
            dayCellList.forEach((cell) => cells.push(cell));
        }
        return { start: dayStart, end: cells[cells.length - 1].end, cells, groups };
    }
    const cells = hourCells(dayStart, 1, tz);
    const label = zoned(dayStart, tz).setLocale('en-GB').toLocaleString({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    return { start: dayStart, end: cells[cells.length - 1].end, cells, groups: [{ key: 0, label, span: cells.length }] };
}

function shiftStart(ms, mode, direction, tz) {
    const dt = zoned(ms, tz).startOf('day');
    if (mode === 'Month') return dt.startOf('month').plus({ months: direction }).toMillis();
    const step = mode === 'Week' ? 7 : mode === 'Days' ? 3 : 1;
    return dt.plus({ days: direction * step }).toMillis();
}

const BAY_FILTERS = [
    { value: 'all', label: 'All bays' },
    { value: 'available', label: 'Available' },
    { value: 'occupied', label: 'Occupied' },
    { value: 'unavailable', label: 'Out of service' },
    { value: 'attention', label: 'Needs attention' },
];

function startOfTodayMs(tz) {
    return DateTime.now().setZone(tz || 'local').startOf('day').toMillis();
}

function toISODate(ms, tz) {
    return zoned(ms, tz).toISODate();
}

function fromISODate(value, tz) {
    return DateTime.fromISO(value, { zone: tz || 'local' }).startOf('day').toMillis();
}

function matchesBayFilter(bay, filter) {
    switch (filter) {
        case 'available': return bay.status === 'active' && !bay.currentBooking;
        case 'occupied': return !!bay.currentBooking;
        case 'unavailable': return bay.status === 'unavailable';
        case 'attention': return !!attentionReason(bay);
        default: return true;
    }
}

const OUT_OF_SERVICE_REASONS = [
    { value: 'TECHNICAL_ERROR', label: 'Technical issue' },
    { value: 'MAINTENANCE', label: 'Maintenance' },
    { value: 'OTHER', label: 'Other' },
];

function formatDay(value) {
    return value ? value.slice(0, 10) : '?';
}

function fmtDateTime(iso, tz) {
    return iso ? DateTime.fromISO(iso).setZone(tz || 'local').setLocale('en-GB').toFormat('d MMM HH:mm ZZZZ') : '—';
}

function bookingState(status, lo, hi, now) {
    if (status === 'settled') return { label: 'Completed', flavour: 'gray', live: false };
    if (status === 'overstaying') return { label: 'Overstaying', flavour: 'warning', live: true };
    const loMs = lo ? new Date(lo).getTime() : -Infinity;
    const hiMs = hi ? new Date(hi).getTime() : Infinity;
    if (status === 'pending_key') {
        return now < loMs
            ? { label: 'Upcoming', flavour: 'gray', live: false }
            : { label: 'Awaiting key', flavour: 'warning', live: true };
    }
    if (now < loMs) return { label: 'Upcoming', flavour: 'gray', live: false };
    if (now >= hiMs) return { label: 'Ended', flavour: 'gray', live: false };
    return { label: 'In progress', flavour: 'success', live: true };
}

// Most-severe fault that needs staff attention, or null. Out-of-service bays are
// deliberate (shown via the grid hatch + the Out-of-service metric), not a fault.
function attentionReason(bay) {
    if (bay.status === 'unavailable') return null;
    if (bay.position === 'JAMMED') return { flavour: 'danger', text: 'Barrier jammed' };
    if (bay.reachability === 'OFFLINE') return { flavour: 'danger', text: 'Device offline' };
    if (bay.reachability === 'BLIND') return { flavour: 'danger', text: 'Gateway offline' };
    if (bay.overstaying) return { flavour: 'warning', text: 'Overstaying' };
    if (bay.batteryLow) return { flavour: 'warning', text: `Battery low (${bay.batteryPercent}%)` };
    if (bay.reachability === 'UNKNOWN') return { flavour: 'gray', text: 'Telemetry unavailable' };
    if (bay.reachability === 'MISSING') return { flavour: 'gray', text: 'No device mapped' };
    return null;
}

function Toolbar({ startDayMs, setStartDayMs, isToday, scale, setScale, tz }) {
    return (
        <div className={styles.toolbar}>
            <div className={styles.dateGroup}>
                <HollowButton size="small" spaceBelow="none" onClick={() => setStartDayMs((value) => shiftStart(value, scale, -1, tz))}>‹</HollowButton>
                <HollowButton size="small" spaceBelow="none" disabled={isToday} onClick={() => setStartDayMs(startOfTodayMs(tz))}>Today</HollowButton>
                <HollowButton size="small" spaceBelow="none" onClick={() => setStartDayMs((value) => shiftStart(value, scale, 1, tz))}>›</HollowButton>
                <input
                    type="date"
                    className={styles.dateInput}
                    value={toISODate(startDayMs, tz)}
                    onChange={(event) => { if (event.target.value) setStartDayMs(fromISODate(event.target.value, tz)); }}
                />
            </div>
            <div className={styles.segment}>
                {SCALES.map((option) => (
                    <button
                        key={option}
                        type="button"
                        className={css(styles.segmentButton, option === scale && styles.segmentActive)}
                        onClick={() => setScale(option)}
                    >
                        {option}
                    </button>
                ))}
            </div>
        </div>
    );
}

function Filters({ bayFilter, setBayFilter }) {
    return (
        <div className={styles.filtersRow}>
            <select className={styles.filterSelect} value={bayFilter} onChange={(event) => setBayFilter(event.target.value)}>
                {BAY_FILTERS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </select>
        </div>
    );
}

function InventoryNow({ bays }) {
    const total = bays.length;
    const occupied = bays.filter((bay) => bay.currentBooking).length;
    const available = bays.filter((bay) => bay.status === 'active' && !bay.currentBooking).length;
    const outOfService = bays.filter((bay) => bay.status === 'unavailable').length;
    const overstaying = bays.filter((bay) => bay.overstaying).length;

    const cards = [
        { key: 'occupied', value: occupied, caption: 'Occupied', suffix: ` of ${total}` },
        { key: 'available', value: available, caption: 'Available' },
        { key: 'outOfService', value: outOfService, caption: 'Out of service', flavour: 'warning' },
        { key: 'overstaying', value: overstaying, caption: 'Overstaying', exception: true },
    ];

    return (
        <div className={styles.metricGrid}>
            {cards.map((card) => (
                <div key={card.key} className={styles.metricBox}>
                    <span className={card.exception && card.value > 0 ? styles.metricValueException : styles.metricValue}>
                        {card.value}
                        {card.suffix ? <span className={styles.metricLabel}>{card.suffix}</span> : null}
                    </span>
                    <span className={styles.metricLabel}>
                        {card.flavour && card.value > 0 ? <StateDot flavour={card.flavour}>{card.caption}</StateDot> : card.caption}
                    </span>
                </div>
            ))}
        </div>
    );
}

function Movements({ timelineByBay, start, end, unavailable, loading }) {
    if (loading) return <div className={styles.muted}>Loading schedule…</div>;
    if (unavailable) return <div className={styles.muted}>Schedule unavailable — movements can't be shown right now.</div>;
    let arrivals = 0;
    let departures = 0;
    Object.keys(timelineByBay).forEach((bayId) => {
        timelineByBay[bayId].forEach((booking) => {
            const lo = booking.lo ? new Date(booking.lo).getTime() : null;
            const hi = booking.hi ? new Date(booking.hi).getTime() : null;
            if (lo !== null && lo >= start && lo < end) arrivals += 1;
            if (hi !== null && hi >= start && hi < end) departures += 1;
        });
    });
    return (
        <div>
            <div className={styles.movementRow}><span className={styles.movementLabel}>Arrivals</span><span>{`${arrivals} expected`}</span></div>
            <div className={styles.movementRow}><span className={styles.movementLabel}>Departures</span><span>{`${departures} expected`}</span></div>
        </div>
    );
}

function AffectedBookingsDialog({ open, bayLabel, bookings, onClose }) {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{`${bayLabel} is now out of service`}</DialogTitle>
            <DialogContent>
                {bookings && bookings.length > 0 ? (
                    <>
                        <DialogContentText>Contact these guests — nothing has been moved automatically.</DialogContentText>
                        <Table>
                            <THead>
                                <tr><th>Guest</th><th>Plate</th><th>Window</th></tr>
                            </THead>
                            <TBody>
                                {bookings.map((booking) => (
                                    <tr key={booking.bookingId}>
                                        <td>{booking.guestName || <span className={styles.muted}>—</span>}</td>
                                        <td>{booking.plate}</td>
                                        <td>{`${formatDay(booking.startDay)} → ${formatDay(booking.endDay)}`}</td>
                                    </tr>
                                ))}
                            </TBody>
                        </Table>
                    </>
                ) : (
                    <DialogContentText>No active or upcoming bookings on this bay.</DialogContentText>
                )}
            </DialogContent>
            <DialogActions>
                <Button size="small" spaceBelow="none" onClick={onClose}>Done</Button>
            </DialogActions>
        </Dialog>
    );
}

const TONE_FLAVOUR = { success: 'success', danger: 'danger', info: 'gray' };

function PreviewRow({ label, children }) {
    return (
        <div className={styles.previewRow}>
            <span className={styles.muted}>{label}</span>
            <span>{children}</span>
        </div>
    );
}

function BookingPreviewModal({ open, booking, bay, canOverride, onClose, onOpenBookingPage, onCommanded, tz }) {
    const [override, { loading: commanding }] = useMutation(parklioBookingBarrierOverrideMutation);
    const [message, setMessage] = useState(null);
    const [reasonOpen, setReasonOpen] = useState(false);

    const bookingId = booking ? booking.bookingId : null;
    useEffect(() => { setMessage(null); setReasonOpen(false); }, [bookingId]);

    const now = Date.now();
    const state = booking ? bookingState(booking.status, booking.lo, booking.hi, now) : null;
    const isLive = state ? state.live : false;
    const device = bay ? deviceDescriptor(bay) : null;
    const room = booking && booking.guestRoom ? booking.guestRoom.trim() : '';
    const guest = booking && booking.guestName ? booking.guestName : '—';
    const zoneSuffix = bay && bay.zone ? ` · ${bay.zone}` : '';
    const carPresent = bay ? bay.carPresent === true : false;
    const canCommand = isLive && canOverride && bay && bay.reachability === 'ONLINE';

    const runCommand = async (action, reason) => {
        setMessage(null);
        let result;
        try {
            const res = await override({ variables: { bookingId, action, reason: reason || null } });
            const outcome = res && res.data && res.data.parklioBookingBarrierOverride && res.data.parklioBookingBarrierOverride.outcome;
            result = OUTCOME_MESSAGE[outcome] || UNKNOWN_OUTCOME_MESSAGE;
        } catch (err) {
            result = { text: 'The command could not be sent. Please try again.', tone: 'danger' };
        }
        setMessage(result);
        if (onCommanded) onCommanded();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            {booking ? (
                <>
                    <DialogTitle>{booking.plate}</DialogTitle>
                    <DialogContent>
                        <PreviewRow label="Guest">{room ? `${guest} · Room ${room}` : guest}</PreviewRow>
                        <PreviewRow label="Assigned bay">{bay ? `${bay.label}${zoneSuffix}` : '—'}</PreviewRow>
                        <PreviewRow label="Arrival window">{fmtDateTime(booking.lo, tz)}</PreviewRow>
                        <PreviewRow label="Departure window">{fmtDateTime(booking.hi, tz)}</PreviewRow>
                        <PreviewRow label="Booking status"><StateDot flavour={state.flavour}>{state.label}</StateDot></PreviewRow>
                        {isLive && device ? (
                            <PreviewRow label="Bollard state"><StateDot flavour={device.flavour}>{device.text}</StateDot></PreviewRow>
                        ) : null}
                        {isLive && bay && bay.reachability === 'ONLINE' ? (
                            <PreviewRow label="Car on bay (now)">{bay.carPresent ? 'Yes' : 'No'}</PreviewRow>
                        ) : null}
                        {canCommand ? (
                            <div className={styles.previewActions}>
                                <Button size="small" spaceBelow="none" disabled={commanding} onClick={() => runCommand('OPEN')}>Lower bollard</Button>
                                <HollowButton size="small" spaceBelow="none" disabled={commanding || carPresent} title={carPresent ? 'A vehicle is on the bay — cannot raise' : undefined} onClick={() => setReasonOpen(true)}>Raise bollard</HollowButton>
                            </div>
                        ) : null}
                        {message ? (
                            <div className={styles.previewMessage}><StateDot flavour={TONE_FLAVOUR[message.tone] || 'gray'}>{message.text}</StateDot></div>
                        ) : null}
                    </DialogContent>
                    <DialogActions>
                        <HollowButton size="small" spaceBelow="none" onClick={onClose}>Back</HollowButton>
                        <Button size="small" spaceBelow="none" onClick={() => onOpenBookingPage(booking.bookingId)}>Open full booking</Button>
                    </DialogActions>
                    <ReasonDialog
                        open={reasonOpen}
                        title="Raise bollard"
                        description="Raising the bollard blocks the bay. Select a reason to continue."
                        reasons={OVERRIDE_REASONS}
                        confirmLabel="Raise bollard"
                        onClose={() => setReasonOpen(false)}
                        onConfirm={({ reason }) => { setReasonOpen(false); runCommand('CLOSE', reason); }}
                    />
                </>
            ) : null}
        </Dialog>
    );
}

function OperationsTab({ canManage, canOverride, tz }) {
    const [startDayMs, setStartDayMs] = useState(() => startOfTodayMs(tz));
    const [scale, setScale] = useState('Hours');
    const [bayFilter, setBayFilter] = useState('all');
    const [menu, setMenu] = useState({ bay: null, anchor: null });
    const [reasonBay, setReasonBay] = useState(null);
    const [closeTarget, setCloseTarget] = useState(null);
    const [affected, setAffected] = useState(null);
    const [preview, setPreview] = useState(null);
    const [actionError, setActionError] = useState(null);

    const history = useHistory();
    const routeMatch = useRouteMatch();
    const openBookingPreview = (booking, bay) => setPreview({ bookingId: booking.bookingId, bayId: bay.id });
    const goToBookingPage = (bookingId) => {
        const base = routeMatch.url.replace(/\/parking-operations\/?$/, '');
        history.push(`${base}/bookings/${bookingId}`);
    };

    const { start, end, cells, groups } = buildSlots(startDayMs, scale, tz);
    const windowMs = end - start;
    const now = Date.now();
    const isToday = startDayMs === startOfTodayMs(tz);

    const baysQuery = useQuery(listParkingOperationsBays, { fetchPolicy: 'network-only', pollInterval: 30000 });
    const timelineQuery = useQuery(listBayTimeline, {
        variables: { startDay: new Date(start).toISOString(), endDay: new Date(end).toISOString() },
        fetchPolicy: 'network-only',
        pollInterval: 60000,
    });
    const [setOutOfService] = useMutation(setBayOutOfServiceMutation);
    const [returnToService] = useMutation(returnBayToServiceMutation);
    const [override] = useMutation(parklioBookingBarrierOverrideMutation);

    // Fall back to previousData so a failed background poll (network errors null out
    // data regardless of errorPolicy) keeps the last known bays on screen instead of
    // blanking the page; the stale banner below flags that the refresh failed.
    const baysData = baysQuery.data || baysQuery.previousData;
    const bays = (baysData && baysData.parklioOperationsBaysForInstallation) || [];
    const timelineError = Boolean(timelineQuery.error);
    // Reuse previousData only for an errored poll of the SAME window — never after a
    // failed window change, which would paint the previous window under the new axis.
    const timelineWindowKey = `${new Date(start).toISOString()}|${new Date(end).toISOString()}`;
    const lastGoodTimelineWindow = useRef(null);
    useEffect(() => {
        if (timelineQuery.data) lastGoodTimelineWindow.current = timelineWindowKey;
    }, [timelineQuery.data, timelineWindowKey]);
    const timelineData =
        timelineQuery.data ||
        (timelineError && lastGoodTimelineWindow.current === timelineWindowKey
            ? timelineQuery.previousData
            : undefined);
    const timelineBays = (timelineData && timelineData.parklioBayTimeline) || [];
    const timelineByBay = {};
    timelineBays.forEach((entry) => { timelineByBay[entry.bayId] = entry.bookings; });
    const timelineLoadingNoData = timelineQuery.loading && !timelineData;
    const timelineUnavailable = timelineError && !timelineData;
    const timelineStale = timelineError && Boolean(timelineData);

    const filteredBays = bays.filter((bay) => matchesBayFilter(bay, bayFilter));

    // Resolve the open preview from live query results each render rather than a frozen
    // snapshot, so polling-driven changes (a car arriving, a booking settling) reflect
    // immediately in the modal's device state and command controls.
    const previewBay = preview ? (bays.find((bay) => bay.id === preview.bayId) || null) : null;
    const previewBooking = preview
        ? ((timelineByBay[preview.bayId] || []).find((booking) => booking.bookingId === preview.bookingId) || null)
        : null;
    const previewMissing = !!preview && !timelineLoadingNoData && (!previewBooking || !previewBay);
    useEffect(() => { if (previewMissing) setPreview(null); }, [previewMissing]);

    const refetchAll = async () => {
        try { await baysQuery.refetch(); } catch (err) { /* best effort */ }
        try { await timelineQuery.refetch(); } catch (err) { /* best effort */ }
    };

    const confirmOutOfService = async ({ reason }) => {
        const bay = reasonBay;
        setActionError(null);
        try {
            const res = await setOutOfService({ variables: { bayId: bay.id, reason } });
            const result = res && res.data && res.data.parklioSetBayOutOfService;
            setReasonBay(null);
            if (result) {
                setAffected({ bayLabel: bay.label, bookings: result.affectedBookings || [] });
            } else {
                setActionError('The bay could not be taken out of service.');
            }
        } catch (err) {
            setReasonBay(null);
            setActionError('The bay could not be taken out of service. Please try again.');
        }
        await refetchAll();
    };

    const doReturn = async (bay) => {
        setActionError(null);
        try {
            await returnToService({ variables: { bayId: bay.id } });
        } catch (err) {
            setActionError('The bay could not be returned to service. Please try again.');
        }
        await refetchAll();
    };

    const runOverride = async (bookingId, action, reason) => {
        setActionError(null);
        try {
            const res = await override({ variables: { bookingId, action, reason: reason || null } });
            const result = res && res.data && res.data.parklioBookingBarrierOverride;
            const outcome = result && result.outcome;
            if (outcome !== 'SUCCESS') setActionError((OUTCOME_MESSAGE[outcome] || UNKNOWN_OUTCOME_MESSAGE).text);
        } catch (err) {
            setActionError('The barrier command could not be sent. Please try again.');
        }
        await refetchAll();
    };

    const doOpen = (bay) => runOverride(bay.currentBooking.bookingId, 'OPEN', null);
    const confirmClose = async ({ reason }) => {
        const target = closeTarget;
        setCloseTarget(null);
        if (target) await runOverride(target.bookingId, 'CLOSE', reason);
    };

    const openMenu = (bay, anchor) => setMenu({ bay, anchor });
    const closeMenu = () => setMenu({ bay: null, anchor: null });
    const menuBay = menu.bay ? bays.find((bay) => bay.id === menu.bay.id) || null : null;

    if (baysQuery.error && !baysData) return <LoadingError error={baysQuery.error} />;
    if (baysQuery.loading && bays.length === 0) return <Well><Loading /></Well>;

    return (
        <>
            {actionError ? <Well flavour="danger" style={{ marginBottom: 12 }}>{actionError}</Well> : null}
            {baysQuery.error ? <Well flavour="warning" style={{ marginBottom: 12 }}>Live data couldn't be refreshed — showing the last known state. Retrying in the background.</Well> : null}

            <Toolbar startDayMs={startDayMs} setStartDayMs={setStartDayMs} isToday={isToday} scale={scale} setScale={setScale} tz={tz} />
            <Filters bayFilter={bayFilter} setBayFilter={setBayFilter} />

            <section className={styles.section}>
                <h2 className={styles.sectionHeading}>Inventory now</h2>
                <InventoryNow bays={bays} />
            </section>

            <section className={styles.section}>
                <h2 className={styles.sectionHeading}>Movements</h2>
                <Movements timelineByBay={timelineByBay} start={start} end={end} unavailable={timelineUnavailable} loading={timelineLoadingNoData} />
            </section>

            <section className={styles.section}>
                <h2 className={styles.sectionHeading}>Bay inventory</h2>
                {timelineUnavailable ? (
                    <div className={css(styles.muted, { marginBottom: 12 })}>Live allocations couldn't be loaded — bays and device state are current; the booking schedule is unavailable right now.</div>
                ) : timelineStale ? (
                    <div className={css(styles.muted, { marginBottom: 12 })}>The schedule couldn't be refreshed — showing the last known allocations.</div>
                ) : null}
                {bays.length === 0 ? (
                    <Well>No bays to show for this hotel.</Well>
                ) : (
                    <BayInventory
                        bays={filteredBays}
                        timelineByBay={timelineByBay}
                        cells={cells}
                        groups={groups}
                        start={start}
                        windowMs={windowMs}
                        now={now}
                        canManage={canManage}
                        onMenu={openMenu}
                        onOpenBooking={openBookingPreview}
                    />
                )}
            </section>

            <Menu anchorEl={menu.anchor} open={Boolean(menu.anchor)} onClose={closeMenu}>
                {menuBay && canManage && menuBay.status === 'active' ? (
                    <MenuItem onClick={() => { setReasonBay(menuBay); closeMenu(); }}>Take out of service</MenuItem>
                ) : null}
                {menuBay && canManage && menuBay.status === 'unavailable' ? (
                    <MenuItem onClick={() => { const bay = menuBay; closeMenu(); doReturn(bay); }}>Return to service</MenuItem>
                ) : null}
                {menuBay && canOverride && menuBay.currentBooking && menuBay.reachability === 'ONLINE' ? (
                    <MenuItem onClick={() => { const bay = menuBay; closeMenu(); doOpen(bay); }}>Lower bollard</MenuItem>
                ) : null}
                {menuBay && canOverride && menuBay.currentBooking && menuBay.reachability === 'ONLINE' ? (
                    <MenuItem disabled={menuBay.carPresent === true} title={menuBay.carPresent === true ? 'A vehicle is on the bay — cannot raise' : undefined} onClick={() => { setCloseTarget({ bookingId: menuBay.currentBooking.bookingId, label: menuBay.label }); closeMenu(); }}>Raise bollard</MenuItem>
                ) : null}
            </Menu>

            <ReasonDialog
                open={!!reasonBay}
                title={reasonBay ? `Take ${reasonBay.label} out of service` : ''}
                description="New bookings won't be assigned to this bay. Current guests are shown so reception can contact them — nothing is moved automatically."
                reasons={OUT_OF_SERVICE_REASONS}
                confirmLabel="Take out of service"
                destructive
                onConfirm={confirmOutOfService}
                onClose={() => setReasonBay(null)}
            />

            <ReasonDialog
                open={!!closeTarget}
                title={closeTarget ? `Raise the bollard at ${closeTarget.label}` : ''}
                description="Raises the bollard to protect the bay. A reason is required and recorded."
                reasons={OVERRIDE_REASONS}
                confirmLabel="Raise bollard"
                onConfirm={confirmClose}
                onClose={() => setCloseTarget(null)}
            />

            <AffectedBookingsDialog
                open={!!affected}
                bayLabel={affected ? affected.bayLabel : ''}
                bookings={affected ? affected.bookings : []}
                onClose={() => setAffected(null)}
            />

            <BookingPreviewModal
                open={!!preview}
                booking={previewBooking}
                bay={previewBay}
                canOverride={canOverride}
                onClose={() => setPreview(null)}
                onOpenBookingPage={goToBookingPage}
                onCommanded={refetchAll}
                tz={tz}
            />
        </>
    );
}

function AuditTab({ tz }) {
    const { data = {} } = useQuery(listParkingOperationsBays, { fetchPolicy: 'cache-and-network' });
    const bays = data.parklioOperationsBaysForInstallation || [];
    const bayLabelById = bays.reduce((acc, bay) => { acc[bay.id] = bay.label; return acc; }, {});
    return <ActivityLog bayLabelById={bayLabelById} tz={tz} />;
}

function ParkingOperationsPage({ installation }) {
    const installationId = installation && installation.id;
    const tz = installation && installation.timeZoneName;
    const canManage = authManager.hasPermission(PERMISSIONS.PARKING_OPERATIONS_MANAGE, installationId);
    const canOverride = canManage && authManager.hasPermission(PERMISSIONS.BOOKINGS_MANAGE, installationId);
    const [tab, setTab] = useState('operations');

    const tabs = [
        { key: 'operations', label: 'Operations' },
        { key: 'audit', label: 'Audit' },
    ];

    return (
        <div>
            <Helmet><title>Parking Operations</title></Helmet>
            <div className={styles.tabBar}>
                {tabs.map((item) => (
                    <button
                        key={item.key}
                        type="button"
                        className={css(styles.tab, tab === item.key && styles.tabActive)}
                        onClick={() => setTab(item.key)}
                    >
                        {item.label}
                    </button>
                ))}
            </div>
            <div className={styles.header}>
                <h1 className={styles.pageTitle}>Parking operations</h1>
                {installation ? <div className={styles.pageDescription}>{installation.name}</div> : null}
            </div>
            {/* Remount on installation switch so the x-installation-id-scoped queries refetch. */}
            <div key={installationId}>
                {tab === 'operations' ? <OperationsTab canManage={canManage} canOverride={canOverride} tz={tz} /> : null}
                {tab === 'audit' ? <AuditTab tz={tz} /> : null}
            </div>
        </div>
    );
}

/* The sidebar only links here for Parklio-enabled hotels, but a bookmarked or
   hand-typed URL would still mount the page — and start its polling queries —
   for a hotel with Parklio switched off. Gate the route the same way
   QrCodesRoute does, in a wrapper so the page's own hooks are never called
   conditionally. */
function ParkingOperationsRoute({ installation, facility }) {
    if (!isParklioBayAccessEnabled(facility)) return null;

    return <ParkingOperationsPage installation={installation} />;
}

export default withInstallation(ParkingOperationsRoute);
